import React, { useState } from "react";

export type ThemeMode = "light" | "dark";

export type User = {
	_id: string;
	name: string;
	email: string;
	phone: string;
	password: string;
	createdAt: string;
	updatedAt: string;
	__v: number;
	accountVerifiedAt: string;
	resetToken: string;
	resetTokenExpiredAt: string;
	permissions: any[];
	role: string;
	notifications: any[];
	preference: Preference;
	tokens: any[];
	deleted: boolean;
	teams: Team[];
};
type Team = {
	_id: string;
	name: string;
	description: string;
	createdAt: string;
	updatedAt: string;
	integrations: {
		github: {
			installationId?: string | null;
			account: {
				login: string;
			};
		};
	};
	__v: number;
};
type Preference = {
	notification: Notification;
};
type Notification = {
	authLogin: AuthLogin;
	authLogout: AuthLogin;
	authResetPassword: AuthLogin;
};
type AuthLogin = {
	push: boolean;
	email: boolean;
};

const AppContext = React.createContext({
	isLoggedIn: false,
	role: "",
	user: {} as User,
	team: {} as Team,
	theme: "light" as ThemeMode,
	login: (user: User) => {},
	logout: () => {},
	setUser: (user: User) => {},
	setTeam: (team: Team) => {},
	setTheme: (theme: ThemeMode) => {},
});

export const AppContextProvider = ({ children }: { children: React.ReactNode }) => {
	const initialLoggedIn: boolean = localStorage.getItem("logged_in") === "true";
	const initialRole: string | null = localStorage.getItem("role");
	const initialUser: string | null = localStorage.getItem("user");
	const initialTeam: string | null = localStorage.getItem("team");
	const initialTheme: ThemeMode = (localStorage.getItem("theme") as ThemeMode) || "light";
	const [isLoggedIn, setLoggedIn] = useState(initialLoggedIn);
	const [role, setRole] = useState<string | null>(initialRole);
	const [user, setUser] = useState<User>(
		initialUser ? (initialUser !== "undefined" ? JSON.parse(initialUser) : {}) : {}
	);
	const [team, setTeam] = useState<Team>(
		initialTeam ? (initialTeam !== "undefined" ? JSON.parse(initialTeam) : {}) : {}
	);
	const [theme, setTheme] = useState<ThemeMode>(initialTheme);

	const loginHandler = (user: User) => {
		setLoggedIn(true);
		setRole(user.role);
		localStorage.setItem("logged_in", "true");
		localStorage.setItem("role", user.role);
		localStorage.setItem("user", JSON.stringify(user));
	};

	const logoutHandler = () => {
		setLoggedIn(false);
		localStorage.removeItem("logged_in");
		localStorage.removeItem("role");
		localStorage.removeItem("year");
		localStorage.removeItem("user");
	};

	const setUserHandler = (user: User) => {
		setUser(user);
		localStorage.setItem("user", JSON.stringify(user));
	};

	const setTeamHandler = (team: Team) => {
		setTeam(team);
		localStorage.setItem("team", JSON.stringify(team));
	};

	const setThemeHandler = (newTheme: ThemeMode) => {
		setTheme(newTheme);
		localStorage.setItem("theme", newTheme);
	};

	const changeRoleHandler = (role: string) => {
		setRole(role);
		localStorage.setItem("role", role);
	};

	const contextValue = {
		isLoggedIn: isLoggedIn,
		role: role || "",
		user: user,
		team: team,
		theme: theme,
		login: loginHandler,
		logout: logoutHandler,
		setUser: setUserHandler,
		setTeam: setTeamHandler,
		setTheme: setThemeHandler,
	};

	return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export default AppContext;
